import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ClipboardList, ArrowRight, PencilLine } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import StatusPill from "@/components/marketplace/StatusPill";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

/** `/dashboard/requests` — service requests (drafts + submitted) plus any legacy project requests. */
export default function CustomerRequestsListPage() {
  const [items, setItems] = useState([]);
  const [legacy, setLegacy] = useState([]);
  const [loading, setLoading] = useState(true);
  useDocumentTitle("My requests");

  useEffect(() => {
    Promise.all([
      api.get("/service-requests/mine").then((r) => setItems(r.data.items || [])).catch(() => setItems([])),
      api.get("/requests", { params: { scope: "mine" } }).then((r) => setLegacy((r.data.items || []).filter((x) => x.flow !== "smart"))).catch(() => setLegacy([])),
    ]).finally(() => setLoading(false));
  }, []);

  const empty = !items.length && !legacy.length;
  return (
    <DashboardShell>
      <div className="max-w-5xl" data-testid="my-requests-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">My requests</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Your service requests</h1>
          </div>
          <Link to="/request-a-service" className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2 font-medium hover:bg-[#11485a]" data-testid="my-requests-new">
            <Plus className="h-4 w-4" /> New request
          </Link>
        </div>
        {loading ? (
          <div className="text-[#252A34]/60">Loading…</div>
        ) : empty ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <ClipboardList className="h-6 w-6 mx-auto text-[#165a71]" />
            <div className="mt-2 font-display text-lg text-[#071b22] font-semibold">No requests yet.</div>
            <div className="text-sm text-[#252A34]/70 mt-1">Tell us what you need — matched pros send you estimates.</div>
            <Link to="/request-a-service" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-medium" data-testid="my-requests-empty-cta">Request a service <ArrowRight className="h-4 w-4" /></Link>
          </div>
        ) : (
          <div className="grid gap-3">
            {items.map((r) => {
              const draft = r.status === "draft";
              return (
                <Link key={r.id} to={draft ? `/request-a-service/${r.id}` : `/dashboard/service-requests/${r.id}`} className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 flex items-start gap-4 hover:border-[#165a71]/40" data-testid={`my-request-${r.id}`}>
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${draft ? "bg-amber-50 text-amber-700" : "bg-[#165a71]/10 text-[#165a71]"}`}>{draft ? <PencilLine className="h-5 w-5" /> : <ClipboardList className="h-5 w-5" />}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3"><div className="font-display font-semibold text-[#071b22] truncate">{r.title}</div><StatusPill status={r.status} /></div>
                    <div className="text-xs text-[#252A34]/60 mt-1">
                      {draft ? `Draft · ${r.completeness?.score ?? 0}% complete · continue where you left off` : `${r.proposal_count || 0} estimate${r.proposal_count === 1 ? "" : "s"} · ${r.matched_count || 0} pros matched · sent ${r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}`}
                    </div>
                  </div>
                </Link>
              );
            })}
            {legacy.map((r) => (
              <Link key={r.id} to={`/requests/${r.id}`} className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 flex items-start gap-4 hover:border-[#165a71]/40" data-testid={`my-legacy-request-${r.id}`}>
                <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500"><ClipboardList className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3"><div className="font-display font-semibold text-[#071b22]">{r.title}</div><StatusPill status={r.status} /></div>
                  <div className="text-xs text-[#252A34]/60 mt-1">{r.proposal_count || 0} proposals · posted {r.published_at ? new Date(r.published_at).toLocaleDateString() : "—"}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
